import { gql } from '@apollo/client';

import { ErrorsAttributes } from '../constants';

export const LABEL_OBJECT_TYPES = ['site', 'group', 'task'];
export const DEFAULT_LABEL_BACKGROUND_COLOR = '#e0e0e0';
export const LABEL_HEX_PATTERN = /^#([0-9A-Fa-f]{3}){1,2}$/;

export const labelStateDefaults = {
  name: '',
  description: '',
  backgroundColor: DEFAULT_LABEL_BACKGROUND_COLOR,
  objectType: null,
};

export const isLabelObjectType = (value) => LABEL_OBJECT_TYPES.includes(value);

export const labelObjectTypeModelKey = (objectType) => {
  if (objectType === 'site') return 'site.singular';
  if (objectType === 'group') return 'group.singular';
  return 'task.singular';
};

export const labelObjectTypePluralModelKey = (objectType) => {
  if (objectType === 'site') return 'site.plural';
  if (objectType === 'group') return 'group.plural';
  return 'task.plural';
};

const expandHexDigits = (hex) => {
  const digits = (hex || '').replace('#', '');
  if (digits.length === 3) return digits.split('').map((char) => char + char).join('');
  return digits;
};

export const expandLabelHex = (hex) => {
  if (!LABEL_HEX_PATTERN.test(hex || '')) return DEFAULT_LABEL_BACKGROUND_COLOR;
  return `#${expandHexDigits(hex)}`;
};

// APCA 0.0.98G-4g perceptual contrast. Keep in sync with HexColor in dubbot-api.
// WCAG 2's luminance midpoint picks dark text for mid blues such as #6C85A7;
// this comparison picks whichever of black or white text reads more clearly.
const SRGB_GAMMA = 2.4;
const RED_LUMINANCE_WEIGHT = 0.2126729;
const GREEN_LUMINANCE_WEIGHT = 0.7151522;
const BLUE_LUMINANCE_WEIGHT = 0.0721750;
// Lift near-black luminance so pure black does not exaggerate contrast.
const NEAR_BLACK_LUMINANCE = 0.022;
const NEAR_BLACK_POWER = 1.414;
// Exponents for dark text on a lighter background, then the reverse.
const DARK_TEXT_BACKGROUND_POWER = 0.56;
const DARK_TEXT_POWER = 0.57;
const LIGHT_TEXT_BACKGROUND_POWER = 0.65;
const LIGHT_TEXT_POWER = 0.62;
const CONTRAST_SCALE = 1.14;
const CONTRAST_OFFSET = 0.027;
const MIN_CONTRAST = 0.1;

const luminance = (digits) => {
  const [red, green, blue] = digits.match(/../g).map((part) => (parseInt(part, 16) / 255) ** SRGB_GAMMA);
  return (RED_LUMINANCE_WEIGHT * red) + (GREEN_LUMINANCE_WEIGHT * green) + (BLUE_LUMINANCE_WEIGHT * blue);
};

const liftNearBlack = (value) => (
  value > NEAR_BLACK_LUMINANCE ? value : value + ((NEAR_BLACK_LUMINANCE - value) ** NEAR_BLACK_POWER)
);

const perceptualContrast = (textLuminance, backgroundLuminance) => {
  const text = liftNearBlack(textLuminance);
  const background = liftNearBlack(backgroundLuminance);

  // Dark text on a lighter background.
  if (background > text) {
    const contrast = ((background ** DARK_TEXT_BACKGROUND_POWER) - (text ** DARK_TEXT_POWER)) * CONTRAST_SCALE;
    return contrast < MIN_CONTRAST ? 0 : contrast - CONTRAST_OFFSET;
  }

  // Light text on a darker background.
  const contrast = ((background ** LIGHT_TEXT_BACKGROUND_POWER) - (text ** LIGHT_TEXT_POWER)) * CONTRAST_SCALE;
  return contrast > -MIN_CONTRAST ? 0 : contrast + CONTRAST_OFFSET;
};

export const getLabelTextColor = (hex) => {
  const digits = expandHexDigits(expandLabelHex(hex));
  if (!/^[0-9A-Fa-f]{6}$/.test(digits)) return 'dark';

  const backgroundLuminance = luminance(digits);
  const darkContrast = Math.abs(perceptualContrast(0, backgroundLuminance));
  const lightContrast = Math.abs(perceptualContrast(1, backgroundLuminance));
  return darkContrast >= lightContrast ? 'dark' : 'light';
};

export const LabelFragment = gql`
  fragment LabelFragment on Label {
    id
    name
    description
    backgroundColor
    textColor
    objectType
  }
`;

export const LabelsQuery = gql`
  query LabelsQuery($accountId: String!, $objectType: LabelObjectType, $page: Int!, $perPage: Int!, $sortBy: LabelsSortBy!, $sortOrder: SortOrder!, $search: String) {
    labels(accountId: $accountId, objectType: $objectType, page: $page, perPage: $perPage, sortBy: $sortBy, sortOrder: $sortOrder, search: $search) {
      currentPage
      perPage
      totalPages
      totalEntries
      nodes {
        ...LabelFragment
      }
    }
  }
  ${LabelFragment}
`;

export const LabelsQueryForSelection = gql`
  query LabelsForSelection($accountId: String!, $objectType: LabelObjectType!, $page: Int!, $perPage: Int!, $sortBy: LabelsSortBy!, $sortOrder: SortOrder!, $search: String) {
    labels(accountId: $accountId, objectType: $objectType, page: $page, perPage: $perPage, sortBy: $sortBy, sortOrder: $sortOrder, search: $search) {
      totalEntries
      totalPages
      currentPage
      nodes {
        ...LabelFragment
      }
    }
  }
  ${LabelFragment}
`;

export const LabelQuery = gql`
  query LabelQuery($accountId: String!, $labelId: String!) {
    label(accountId: $accountId, labelId: $labelId) {
      ...LabelFragment
    }
  }
  ${LabelFragment}
`;

export const CreateLabelMutation = gql`
  mutation CreateLabel($accountId: String!, $objectType: LabelObjectType!, $name: String!, $description: String, $backgroundColor: String) {
    createLabel(accountId: $accountId, objectType: $objectType, name: $name, description: $description, backgroundColor: $backgroundColor) {
      label {
        ...LabelFragment
        ${ErrorsAttributes}
      }
    }
  }
  ${LabelFragment}
`;

export const UpdateLabelMutation = gql`
  mutation UpdateLabel($accountId: String!, $labelId: String!, $name: String, $description: String, $backgroundColor: String) {
    updateLabel(accountId: $accountId, labelId: $labelId, name: $name, description: $description, backgroundColor: $backgroundColor) {
      label {
        ...LabelFragment
        ${ErrorsAttributes}
      }
    }
  }
  ${LabelFragment}
`;

export const DestroyLabelsMutation = gql`
  mutation DestroyLabels($accountId: String!, $labelIds: [String!]!) {
    destroyLabels(accountId: $accountId, labelIds: $labelIds) {
      status
    }
  }
`;
